/**
 * Voice Clone Translation client.
 *
 * - REST endpoints for multipart enrollment (Apollo Server 2 in this
 *   project doesn't carry the Upload scalar, so a tiny REST surface is
 *   simpler than reworking the schema).
 * - GraphQL queries / mutations for status + translation.
 *
 * Auth uses the same getAuthToken() chain as recordingsApi.ts.
 */
import config from "../config";
import { AiFeatureDisabledError } from "./aiApi";
import { getAuthToken } from "./providers";

// ---- Types ----------------------------------------------------------------

export interface VoiceCloneStatus {
  enrolled: boolean;
  language: string | null;
  durationSec: number | null;
  consentAt: string | null;
  updatedAt: string | null;
}

export interface VoiceTranslation {
  messageId: string;
  targetLanguage: string;
  sourceLanguage: string | null;
  status: "pending" | "done" | "failed";
  error: string | null;
  audioUrl: string | null;
  audioDurationSec: number | null;
  transcriptOrig: string | null;
  transcriptTranslated: string | null;
  speakerUserId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---- URL helpers ----------------------------------------------------------

function getGraphqlUrl(): string {
  const apiUrl = config.apiUrl || "/graphql";
  if (apiUrl.startsWith("/")) return window.location.origin + apiUrl;
  return apiUrl;
}

function getRestBase(): string {
  // graphql-server hosts the REST routes at the same origin as the
  // GraphQL endpoint (just under different paths).
  const apiUrl = config.apiUrl || "/graphql";
  if (apiUrl.startsWith("/")) {
    // /graphql → "" (root) on the same host
    return window.location.origin;
  }
  try { return new URL(apiUrl).origin; }
  catch { return window.location.origin; }
}

async function graphqlRequest<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const token = await getAuthToken();
  const resp = await fetch(getGraphqlUrl(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`GraphQL HTTP ${resp.status}: ${text.slice(0, 200)}`);
  }
  const body = await resp.json();
  if (body.errors && body.errors.length > 0) {
    const err = body.errors[0];
    const code = err.extensions?.code;
    if (code === "FEATURE_DISABLED" || code === "AI_UNAVAILABLE" || code === "FEATURE_UNAVAILABLE") {
      throw new AiFeatureDisabledError(err.message || "Voice Clone disabled", code);
    }
    throw new Error(err.message || "GraphQL error");
  }
  return body.data as T;
}

// ---- GraphQL: status + translation ----------------------------------------

const TRANSLATION_FIELDS = `
  messageId targetLanguage sourceLanguage
  status error audioUrl audioDurationSec
  transcriptOrig transcriptTranslated speakerUserId
  createdAt updatedAt
`;

export async function fetchVoiceCloneStatus(): Promise<VoiceCloneStatus> {
  const data = await graphqlRequest<{ voiceCloneStatus: VoiceCloneStatus }>(
    `query { voiceCloneStatus { enrolled language durationSec consentAt updatedAt } }`,
    {},
  );
  return data.voiceCloneStatus;
}

export async function fetchSupportedLanguages(): Promise<string[]> {
  const data = await graphqlRequest<{ voiceCloneSupportedLanguages: string[] }>(
    `query { voiceCloneSupportedLanguages }`,
    {},
  );
  return data.voiceCloneSupportedLanguages || [];
}

export async function fetchVoiceTranslation(
  messageId: string,
  targetLanguage: string,
): Promise<VoiceTranslation | null> {
  const data = await graphqlRequest<{ voiceTranslation: VoiceTranslation | null }>(
    `query($id: ID!, $lang: String!) {
      voiceTranslation(messageId: $id, targetLanguage: $lang) { ${TRANSLATION_FIELDS} }
    }`,
    { id: messageId, lang: targetLanguage.toLowerCase() },
  );
  return data.voiceTranslation;
}

export async function translateVoiceMessage(
  messageId: string,
  targetLanguage: string,
  sourceText: string,
  speakerUserId: string,
  sourceDurationSec?: number,
): Promise<VoiceTranslation> {
  const data = await graphqlRequest<{ translateVoiceMessage: VoiceTranslation }>(
    `mutation($id: ID!, $lang: String!, $text: String!, $speaker: ID!, $dur: Float) {
      translateVoiceMessage(
        messageId: $id,
        targetLanguage: $lang,
        sourceText: $text,
        speakerUserId: $speaker,
        sourceDurationSec: $dur,
      ) { ${TRANSLATION_FIELDS} }
    }`,
    {
      id: messageId,
      lang: targetLanguage.toLowerCase(),
      text: sourceText,
      speaker: speakerUserId,
      dur: typeof sourceDurationSec === "number" && sourceDurationSec > 0
        ? sourceDurationSec
        : null,
    },
  );
  return data.translateVoiceMessage;
}

// ---- REST: enrollment + profile management --------------------------------

export interface EnrollResult {
  ok: boolean;
  userId: string;
  language: string | null;
  durationSec: number | null;
  ttsResp?: unknown;
}

export async function enrollVoice(opts: {
  audio: Blob;
  audioFilename?: string;
  referenceText: string;
  language?: string | null;
  durationSec?: number | null;
  consent: boolean;
}): Promise<EnrollResult> {
  if (!opts.consent) {
    throw new Error("Consent is required to enroll your voice");
  }

  const token = await getAuthToken();
  const fd = new FormData();
  fd.append("audio", opts.audio, opts.audioFilename || "reference.webm");
  fd.append("referenceText", opts.referenceText);
  fd.append("consent", "true");
  if (opts.language) fd.append("language", opts.language);
  if (typeof opts.durationSec === "number") fd.append("durationSec", String(opts.durationSec));

  const resp = await fetch(`${getRestBase()}/voice-clone/enroll`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: token } : {},
    body: fd,
  });

  if (resp.status === 503) {
    throw new AiFeatureDisabledError("Voice Clone disabled", "FEATURE_DISABLED");
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    let parsed: { error?: string; detail?: string } = {};
    try { parsed = JSON.parse(text); } catch { /* keep raw */ }
    throw new Error(parsed.error || parsed.detail || text.slice(0, 200) || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as EnrollResult;
}

export async function deleteVoiceProfile(): Promise<void> {
  const token = await getAuthToken();
  const resp = await fetch(`${getRestBase()}/voice-clone/profile`, {
    method: "DELETE",
    credentials: "include",
    headers: token ? { Authorization: token } : {},
  });
  if (resp.status === 503) {
    throw new AiFeatureDisabledError("Voice Clone disabled", "FEATURE_DISABLED");
  }
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
}

/**
 * Build an absolute URL for a translated-audio path returned by the
 * GraphQL mutation (`/voice-translations/files/<id>__<lang>.mp3`).
 * The browser fetches this through the regular auth-cookie pipeline.
 */
export function buildTranslationAudioUrl(audioUrl: string | null): string | null {
  if (!audioUrl) return null;
  if (audioUrl.startsWith("http://") || audioUrl.startsWith("https://")) return audioUrl;
  return `${getRestBase()}${audioUrl}`;
}

export const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
  de: "German",
  fr: "French",
  it: "Italian",
  pt: "Portuguese",
  ru: "Russian",
  uk: "Ukrainian",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
};
